// 全站图标（导航、卡片、按钮上的符号）都是字体图标，靠 index.html 里的外部样式表把
// Remix Icon / Font Awesome 字体拉进来。若主 CDN 被浏览器、插件或网络拦截，整站图标会
// 一起变成空白框 —— 因为它们本质是字体里的「字」，字体没加载出来自然就显示不出来。
//
// 这里在主 CDN 未生效时自动注入备用 CDN（依次回退）；若所有来源都失败，则通过事件通知
// 前端展示一个温和的降级提示，提醒用户可以刷新重试。

const REMIX_FALLBACKS = [
  "https://cdn.jsdelivr.net/npm/remixicon@4.5.0/fonts/remixicon.css",
  "https://unpkg.com/remixicon@4.5.0/fonts/remixicon.css",
];

const FONT_AWESOME_FALLBACKS = [
  "https://cdn.jsdelivr.net/npm/@fortawesome/fontawesome-free@6.4.0/css/all.min.css",
  "https://unpkg.com/@fortawesome/fontawesome-free@6.4.0/css/all.min.css",
];

// 全站图标字体都不可用时派发的事件名，供前端监听并展示降级提示。
export const ICON_FONT_FAILURE_EVENT = "icon-font-load-failure";

// 用一个隐藏的图标元素探测对应字体是否已生效：图标字体生效后会给元素撑出宽度，
// 未生效（CDN 被拦 / 还没加载出来）时元素宽度为 0。此法不依赖跨域样式读取，稳定可靠。
function isFontReady(probeClass: string): boolean {
  const probe = document.createElement("i");
  probe.className = probeClass;
  probe.style.position = "absolute";
  probe.style.left = "-9999px";
  probe.style.top = "-9999px";
  probe.style.fontSize = "24px";
  probe.style.lineHeight = "1";
  probe.setAttribute("aria-hidden", "true");
  document.body.appendChild(probe);
  const width = probe.offsetWidth;
  document.body.removeChild(probe);
  return width > 0;
}

// 只要 Remix Icon 或 Font Awesome 任一可用，就不算「全站图标都没加载出来」。
function hasAnyIconFont(): boolean {
  return isFontReady("ri-home-line") || isFontReady("fa-solid fa-house");
}

// 依次尝试备用地址，当前一个加载失败（onerror）时自动切到下一个。
function injectFallback(name: string, urls: string[]): void {
  const tryLoad = (index: number) => {
    if (index >= urls.length) return;
    const link = document.createElement("link");
    link.rel = "stylesheet";
    link.href = urls[index];
    link.setAttribute("data-icon-fallback", name);
    link.onerror = () => {
      link.remove();
      tryLoad(index + 1);
    };
    document.head.appendChild(link);
  };
  tryLoad(0);
}

// 检查主 CDN 是否生效，未生效则补齐备用 CDN。可安全重复调用（同一备用源只注入一次）。
function ensureIconFonts(): void {
  if (!document.querySelector('[data-icon-fallback="remixicon"]') && !isFontReady("ri-home-line")) {
    injectFallback("remixicon", REMIX_FALLBACKS);
  }
  if (!document.querySelector('[data-icon-fallback="fontawesome"]') && !isFontReady("fa-solid fa-house")) {
    injectFallback("fontawesome", FONT_AWESOME_FALLBACKS);
  }
}

let failureNotified = false;

// 全部来源都失败时只通知一次，避免重复弹出提示。
function notifyFailureIfNeeded(): void {
  if (failureNotified) return;
  if (hasAnyIconFont()) return;
  failureNotified = true;
  window.dispatchEvent(new CustomEvent(ICON_FONT_FAILURE_EVENT));
}

// 首次检查可能早于主 CDN 加载完成，做几次轮询再判定，避免误判。
export function setupIconFontFallback(): void {
  [800, 2000, 4500].forEach((delay) => {
    window.setTimeout(() => ensureIconFonts(), delay);
  });
  // 备用源注入后需要一点加载时间，再做几次终判；若始终无任何图标字体，则通知前端降级提示。
  [6500, 9000, 12000].forEach((delay) => {
    window.setTimeout(() => {
      ensureIconFonts();
      notifyFailureIfNeeded();
    }, delay);
  });
}